import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Download, Loader2, AlertTriangle, ArrowRight, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const MisFacturas = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);

  const hasFiscalData = user?.country && user?.fiscal_name && user?.fiscal_nif && user?.fiscal_address && user?.fiscal_cp;

  const loadInvoices = useCallback(async () => {
    if (!hasFiscalData) { setLoading(false); return; }
    try {
      const res = await api.get('/user/invoices');
      setInvoices(res.data || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [hasFiscalData]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    setDownloadingId(invoiceId);
    try {
      const res = await api.get(`/user/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar la factura'); }
    setDownloadingId(null);
  };

  const handlePreview = async (invoiceId) => {
    setPreviewLoading(invoiceId);
    try {
      const res = await api.get(`/user/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch { toast.error('Error al previsualizar'); }
    setPreviewLoading(null);
  };

  const fmt = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        <div className="mb-12">
          <div className="decorative-line mb-6" />
          <h1 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4" data-testid="mis-facturas-title">
            Mis Facturas
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Consulta y descarga las facturas de tus pagos.
          </p>
        </div>

        {/* If fiscal data is incomplete */}
        {!hasFiscalData ? (
          <div className="card-soft" data-testid="fiscal-incomplete-notice">
            <div className="flex items-start gap-4 p-2">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-2">
                  Completa tus datos fiscales
                </h3>
                <p className="text-[hsl(var(--text-secondary))] mb-6 leading-relaxed">
                  Para generar tus facturas necesitas completar tus datos fiscales. 
                  Solo tienes que hacerlo una vez.
                </p>
                <Button
                  onClick={() => navigate('/perfil?section=fiscal&redirect=mis-facturas')}
                  className="btn-gold flex items-center gap-2"
                  data-testid="go-to-fiscal-data"
                >
                  Completar datos fiscales
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-[hsl(var(--accent))] animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          /* No invoices yet */
          <div className="card-soft text-center py-16" data-testid="no-invoices-message">
            <FileText className="w-12 h-12 text-[hsl(var(--text-tertiary))]/30 mx-auto mb-4" strokeWidth={1} />
            <p className="text-[hsl(var(--text-secondary))] text-lg font-heading mb-2">
              Aún no tienes facturas generadas
            </p>
            <p className="text-sm text-[hsl(var(--text-tertiary))]">
              Cuando realices un pago, tu factura aparecerá aquí.
            </p>
          </div>
        ) : (
          /* Invoice list */
          <div className="space-y-3" data-testid="invoices-list">
            {invoices.map(inv => (
              <div
                key={inv.id}
                className="card-soft flex items-center justify-between p-5"
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs text-[hsl(var(--text-tertiary))] font-mono" data-testid={`invoice-number-${inv.id}`}>
                      {inv.invoice_number}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      inv.payment_status === 'paid'
                        ? 'bg-emerald-50 text-emerald-600'
                        : 'bg-amber-50 text-amber-600'
                    }`}>
                      {inv.payment_status === 'paid' ? 'Pagada' : 'Pendiente'}
                    </span>
                  </div>
                  <p className="text-sm text-[hsl(var(--text-primary))] font-medium truncate mb-0.5">
                    {inv.concept}
                  </p>
                  <p className="text-xs text-[hsl(var(--text-tertiary))]">
                    {(inv.paid_at || inv.created_at || '').slice(0, 10)}
                  </p>
                </div>
                <div className="flex items-center gap-5 ml-4">
                  <span className="text-xl font-heading text-[hsl(var(--text-primary))]">
                    {fmt(inv.total || inv.amount)} <span className="text-xs text-[hsl(var(--text-tertiary))]">EUR</span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreview(inv.id)}
                    disabled={previewLoading === inv.id}
                    className="flex items-center gap-1.5 text-[hsl(var(--text-secondary))] border-[hsl(var(--border))] hover:bg-[hsl(var(--sage-light))]"
                    data-testid={`preview-pdf-${inv.id}`}
                  >
                    {previewLoading === inv.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Eye className="w-4 h-4" />
                    }
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPdf(inv.id, inv.invoice_number)}
                    disabled={downloadingId === inv.id}
                    className="flex items-center gap-1.5 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/30 hover:bg-[hsl(var(--accent))]/5"
                    data-testid={`download-pdf-${inv.id}`}
                  >
                    {downloadingId === inv.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />
                    }
                    PDF
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PDF Preview Modal */}
        {previewUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="pdf-preview-modal">
            <div className="bg-white rounded-2xl w-full max-w-3xl h-[80vh] flex flex-col shadow-2xl mx-4">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[hsl(var(--border))]">
                <span className="text-sm font-heading text-[hsl(var(--text-primary))]">Vista previa de factura</span>
                <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] transition-colors" data-testid="close-preview">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <iframe src={previewUrl} className="flex-1 w-full rounded-b-2xl" title="Vista previa factura" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MisFacturas;
